import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CoinCanvas, { CoinConfigType, defaultCoinConfig } from './CoinCanvas';
import StarBg, { defaultStarConfig, StarConfig } from './StarBg';
import { AnimationBox, fadeIn, fadeOut, GradientTypography } from './styles';
import React from 'react';
import {
  CelebrationInfo,
  CelebrationType,
} from '@/components/features/casino/game-celebration/state';
import { createPortal } from 'react-dom';
import { floorToN } from '@/components/features/casino/crash/GameBox/utils';
/* ====================== *
 *    celebration content
 * ====================== */
const CrashCelebrationContent = ({ winAmount, multiplier }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <GradientTypography
        as="h1"
        sx={{
          fontSize: '160px',
          fontWeight: '900',
          lineHeight: 1,
          margin: 0,
          [theme.breakpoints.down('md')]: {
            fontSize: '60px',
          },
        }}
      >
        You Win!
      </GradientTypography>
      {(typeof multiplier !== 'undefined' && (
        <GradientTypography
          as="h1"
          sx={{
            fontSize: '72px',
            margin: 0,
            [theme.breakpoints.down('md')]: {
              fontSize: '40px',
            },
          }}
        >
          {floorToN(multiplier, 2)}x
        </GradientTypography>
      )) ||
        ''}
      <GradientTypography
        as="h1"
        sx={{
          fontSize: '72px',
          margin: 0,
          [theme.breakpoints.down('md')]: {
            fontSize: '32px',
          },
        }}
      >
        {winAmount && winAmount}
      </GradientTypography>
    </Box>
  );
};

const RushCelebrationContent = ({ winAmount, rewardType }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <GradientTypography
        as="h1"
        sx={{
          fontSize: '140px',
          fontWeight: '900',
          lineHeight: 1,
          margin: 0,
          [theme.breakpoints.down('md')]: {
            fontSize: '50px',
          },
        }}
      >
        You Win!
      </GradientTypography>
      <GradientTypography
        as="h1"
        sx={{
          fontSize: '160px',
          fontWeight: '900',
          lineHeight: 1,
          margin: 0,
          [theme.breakpoints.down('md')]: {
            fontSize: '60px',
          },
        }}
      >
        {rewardType?.toUpperCase()}
      </GradientTypography>
      <GradientTypography
        as="h1"
        sx={{
          fontSize: '72px',
          margin: 0,
          [theme.breakpoints.down('md')]: {
            fontSize: '40px',
          },
        }}
      >
        {winAmount && winAmount}
      </GradientTypography>
    </Box>
  );
};

/* ====================== *
 *    Celebration Component
 * ====================== */
export interface WinCelebrationProps {
  starConfig?: StarConfig;
  coinConfig?: CoinConfigType;
  show?: boolean;
  info?: CelebrationInfo;
}

const WinCelebrationContent: React.FC<WinCelebrationProps> = ({
  starConfig = defaultStarConfig,
  coinConfig = defaultCoinConfig,
  show = false,
  info,
}: WinCelebrationProps = {}) => {
  const { type, profit, rewardValue, multiplier } = info;
  const CelebrationContent = () => {
    if (type === CelebrationType.Crash) {
      return (
        <CrashCelebrationContent winAmount={profit} multiplier={multiplier} />
      );
    } else if (type === CelebrationType.Rush) {
      return (
        <RushCelebrationContent winAmount={profit} rewardType={rewardValue} />
      );
    } else {
      return null;
    }
  };
  return (
    <AnimationBox
      sx={
        show
          ? {
              opacity: `${show ? 1 : 0}`,
              animation: `${show ? fadeIn : fadeOut} 1s`,
            }
          : {}
      }
      id={'celebration'}
    >
      {show && <StarBg {...starConfig}></StarBg>}
      <CelebrationContent />
      {show && <CoinCanvas {...coinConfig} />}
    </AnimationBox>
  );
};

export default function WinCelebration({ ...props }) {
  return (
    <div>
      {createPortal(<WinCelebrationContent {...props} />, document.body)}
    </div>
  );
}
